import React from 'react'
import { Link } from 'react-router-dom'
import { HiCamera, HiCog, HiLocationMarker, HiCheckCircle } from 'react-icons/hi'
import Header from '../components/Header'

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 py-20 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Snap. Send. Solved.
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            The easiest way to report city issues—just one click away.
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-4xl mx-auto">
            With CitySeeCityFix, reporting potholes, broken streetlights, or damaged signs is effortless. 
            Residents simply open the app, snap a photo, and put the phone back in their pocket. 
            The app does the rest—analyzing, describing, and routing the issue directly to the right city department.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="btn-primary text-lg px-8 py-4"
            >
              Download CitySeeCityFix
            </Link>
            <Link
              to="/cities"
              className="btn-secondary text-lg px-8 py-4"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              We call it "Click & Pocket." One tap is all it takes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <HiCamera className="h-10 w-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Take a Photo
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Open CitySeeCityFix, capture the problem.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <HiCog className="h-10 w-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                AI Evaluation
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                CitySeeCityFix identifies the issue and prepares a clear report.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <HiLocationMarker className="h-10 w-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Automatic Routing
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                The report is sent to the correct city department using GIS data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Residents Love It */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Residents Love It
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Click & Pocket
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  See a problem? Just open the app, snap a photo, and put your phone away. 
                  CitySeeCityFix handles the rest—analyzing, describing, and routing the issue automatically.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Earn Civic Points
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Every report counts toward your score:
                </p>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center">
                    <HiCheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    2 points for being the first to flag an issue
                  </li>
                  <li className="flex items-center">
                    <HiCheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    1 point for confirming an existing problem
                  </li>
                  <li className="flex items-center">
                    <HiCheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    5 points for each neighbor you invite who joins
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Track Progress
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  When your city partners with CitySeeCityFix, you can follow your submissions in real time—see 
                  when they're received, assigned, and resolved.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Rewards & Recognition
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Top contributors can earn city-branded swag and special recognition—turning civic 
                  pride into something you can wear and share.
                </p>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Transparent Impact
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Every photo becomes a data-rich report, complete with location and context. 
                  Residents know their voices are heard, and cities can act faster.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Cities Love It */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Cities Love It
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Smart Routing
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                CitySeeCityFix automatically builds a live directory of public-facing departments. Each new 
                submission is analyzed, categorized, and prioritized based on safety, accessibility, 
                and repair scale—so the right team sees it first.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Seamless Integration
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Plug CitySeeCityFix into existing ticketing or work-order systems through their API. 
                Submissions flow directly into your processes with zero changes required on the city's side.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Custom Branding
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Participating cities get admin access - full control over their issue routing, 
                their logo and branding to align CitySeeCityFix with the city's identity.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                GIS Powered Reports
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Every submission is enriched with location data, submitter information, and 
                contextual details, giving your teams the clearest picture possible before they roll a truck.
              </p>
            </div>
            
            <div className="card lg:col-span-2">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Community Engagement
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                CitySeeCityFix reframes complaints as collaboration. Residents see city staff as partners, 
                not impersonal bureaucracy—building trust, pride, and stronger community ties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Our Vision
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            A city that listens, a community that cares. With CitySeeCityFix, residents feel empowered, 
            city staff gain efficiency, and together we create cleaner, safer, and more connected places to live.
          </p>
          
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Join the movement.
            </h3>
            <p className="text-lg text-primary-100">
              Download CitySeeCityFix today, snap your first report, and start turning civic pride into real change.
            </p>
          </div>
          
          <Link
            to="/login"
            className="inline-block bg-white text-primary-600 font-semibold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors duration-200"
          >
            Get Started Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 dark:bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  className="h-8 w-8 rounded-full"
                  src="/maggie_round.png"
                  alt="CitySeeCityFix"
                />
                <span className="ml-3 text-xl font-bold text-white">
                  CitySeeCityFix
                </span>
              </div>
              <p className="text-gray-400">
                The easiest way to report city issues—just one click away.
              </p>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Residents</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/maggie" className="hover:text-white">Meet Maggie</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">For Cities</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/cities" className="hover:text-white">Learn More</Link></li>
                <li><Link to="/login" className="hover:text-white">Admin Login</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Connect</h3>
              <p className="text-gray-400">
                <a href="https://api.cityseecityfix.com" className="hover:text-white">
                  API Documentation
                </a>
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CitySeeCityFix. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
